import {
  Box,
  Select,
  MenuItem,
  CircularProgress,
  Typography
} from "@mui/material";

import React, { useState, useMemo } from "react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend
} from "recharts";

import { useMilestonePerformanceQuery } from "../../useQuery";

const COLORS = [
  "#1976d2",
  "#2e7d32",
  "#ed6c02",
  "#9c27b0",
  "#d32f2f"
];

const MilestonePerformance = ({ data, tsrId }) => {

  const [selectedStepId, setSelectedStepId] = useState("");

  const milestones = useMemo(() => {

    if (!data) return [];

    const map = new Map();

    data.forEach(row => {

      if (row.milestone && row.teststepid) {

        map.set(row.teststepid, {
          milestone: row.milestone,
          teststepid: row.teststepid
        });

      }

    });

    return Array.from(map.values());

  }, [data]);


  const {
    data: milestoneData,
    isLoading,
    isError
  } = useMilestonePerformanceQuery(tsrId, selectedStepId, {
    enabled: Boolean(selectedStepId)
  });


  const chartData = useMemo(() => {

    if (!milestoneData?.runs) return [];

    const runs = [...milestoneData.runs];
    runs.sort((a, b) => a.run - b.run);

    return runs;

  }, [milestoneData]);


  const devices = useMemo(() => {

    if (!chartData.length) return [];

    const deviceSet = new Set();

    chartData.forEach(run => {

      Object.keys(run).forEach(key => {

        if (key !== "run") deviceSet.add(key);

      });

    });

    return Array.from(deviceSet);

  }, [chartData]);


  const averageMap = useMemo(() => {

    if (!milestoneData?.averages) return {};

    const map = {};

    milestoneData.averages.forEach(a => {
      map[a.device] = a.average;
    });

    return map;

  }, [milestoneData]);


  const normalizedData = useMemo(() => {

    return chartData.map(run => {

      const row = { ...run };

      devices.forEach(device => {

        if (row[device] === undefined) {
          row[device] = null;
        }

        row[`${device}_avg`] = averageMap[device] ?? null;

      });

      return row;

    });

  }, [chartData, devices, averageMap]);


  return (

    <Box>

      {/* Milestone selector row */}

      <Box mb={3}>

        <Select
          value={selectedStepId}
          onChange={(e) => setSelectedStepId(e.target.value)}
          displayEmpty
          size="small"
          sx={{ minWidth: 260 }}
        >

          <MenuItem value="">
            Select Milestone
          </MenuItem>

          {milestones.map(m => (
            <MenuItem key={m.teststepid} value={m.teststepid}>
              {m.milestone}
            </MenuItem>
          ))}

        </Select>

      </Box>


      {isLoading && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Typography color="error">
          Failed to load milestone performance.
        </Typography>
      )}


      {!isLoading && selectedStepId && normalizedData.length > 0 && (

        <Box height={420}>

          <ResponsiveContainer width="100%" height="100%">

            <LineChart data={normalizedData}>

              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="run"
                label={{
                  value: "Test Case Runs",
                  position: "insideBottom",
                  offset: -5
                }}
              />

              <YAxis
                label={{
                  value: "Duration (seconds)",
                  angle: -90,
                  position: "insideLeft"
                }}
              />

              <Tooltip />

              <Legend />

              {devices.map((device, index) => (

                <React.Fragment key={device}>

                  <Line
                    type="monotone"
                    dataKey={device}
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth={3}
                    dot={{ r: 3 }}
                    connectNulls
                    name={device}
                  />

                  <Line
                    type="monotone"
                    dataKey={`${device}_avg`}
                    stroke={COLORS[index % COLORS.length]}
                    strokeDasharray="6 6"
                    strokeWidth={2}
                    dot={false}
                    name={`${device} Avg`}
                  />

                </React.Fragment>

              ))}

            </LineChart>

          </ResponsiveContainer>

        </Box>

      )}


      {!isLoading && selectedStepId && normalizedData.length === 0 && (

        <Typography color="textSecondary">
          No performance data available for this milestone.
        </Typography>

      )}

    </Box>

  );

};

export default MilestonePerformance;